import * as React from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Grid, Box, TextField, Divider,
} from '@material-ui/core';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash';
import useEventListener from '../../lib/useEventListener';
import RepairCard from './RepairCardWithImage';
import RepairInformation from './RepairInformation';
import MetalBinContainer from './MetalBinContainer';
import MetalBinEmptyDialog from './MetalBinEmptyDialog';
import type { IScannedRepair, IRepairItem } from '../marking/MarkingInterfaces';

const MetalCollection: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [barcode, setBarcode] = React.useState<string>();
  const [emptyBin, setEmptyBin] = React.useState<string | undefined>();
  const [emptyBinItem, setEmptyBinItem] = React.useState<IRepairItem>();

  const repairFetcher = (url: string, barcodeParam: string) => axios.get<IScannedRepair>(url, {
    params: {
      repairBarcode: barcodeParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      if (e.code === '404') {
        setBarcode(undefined);
        enqueueSnackbar("Repair doesn't have any resizing jobs that require fillings", { persist: false, variant: 'error' });
      } else {
        setBarcode(undefined);
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      }
    });

  const openRepair = debounce((barcodeInput: string) => {
    if (barcodeInput !== '') {
      setBarcode(barcodeInput);
    }
  }, 500);

  const { data } = useSWR(barcode ? ['/MetalCollection/GetRepair', barcode] : null, (url, repairBarcodeParam) => repairFetcher(url, repairBarcodeParam));

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      barcodeBuffer = `${barcodeBuffer}${e.key}`;
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        openRepair(barcodeBuffer.replace('{*|}', ''));
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  const sendNotification = (messageType: 'Empty Bin' | 'Low Stock', bin:string) => {
    const notificationObject = {
      department: 'Receiving',
      title: messageType,
      note: messageType === 'Empty Bin'
        ? `Bin ${bin} is empty and needs to be replenished.`
        : `Bin ${bin} is low on stock and needs to be reordered.`,
    };

    axios.post('/Notification/SaveNotificationForDepartmentManagers', notificationObject)
      .then(() => {
        setEmptyBin(undefined);
        enqueueSnackbar(messageType === 'Empty Bin'
          ? `Success! You pulled from bin ${bin}, continue by scanning in a new repair.`
          : `Bin ${bin} has been successfully added to the reorder list.`, { variant: 'success' });
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(JSON.stringify(error?.response?.data), { variant: 'error' });
      });
  };

  const RepairItemMap = (
    data?.repairItems?.map((repairItem:IRepairItem) => (
      <Grid item xs={12} key={repairItem.repairItemId}>
        <RepairInformation RepairItem={repairItem} />
      </Grid>
    ))
  );

  if (!data) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Scan in repair to determine quantity of parts you’ll need and which bin(s) to pull them from.'}</Alert>
        </Grid>
        <Grid item>
          <TextField
            id="metal-collection-barcode-input"
            variant="outlined"
            size="small"
            onKeyPress={
            (event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                openRepair((event.target as HTMLInputElement).value);
              }
            }
            }
          />
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      {
      emptyBin && emptyBinItem
      && (
      <MetalBinEmptyDialog
        emptyBin={emptyBin}
        emptyBinItem={emptyBinItem}
        sendNotification={sendNotification}
        onClose={() => {
          setEmptyBin(undefined);
          setEmptyBinItem(undefined);
        }}
      />
      )
      }
      <Grid container spacing={0}>
        <Grid container item spacing={1} xs={9}>
          <Grid item xs={1} />
          <Grid container item spacing={2} xs={11}>
            <Grid item xs={12}>
              <Alert
                severity="info"
              >
                {'Collect the correct type and quantity of metal pieces from designated bin(s), and place in repair bag.'}
              </Alert>
            </Grid>
            <Grid item lg={6} xs={12}>
              <RepairCard
                barcode={barcode || ''}
                secondaryText={
                  `${data.repairItems?.length} item${data.repairItems?.length === 1 ? '' : 's'}`
                }
              />
            </Grid>
            <Grid item xs={12}>
              <MetalBinContainer
                repairItems={data.repairItems.filter((repairItem) => repairItem.sizeChange)}
                sendNotification={sendNotification}
                setEmptyBin={setEmptyBin}
                setEmptyItemBin={setEmptyBinItem}
              />
            </Grid>
            {RepairItemMap}
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Divider orientation="vertical" />
      </Grid>
    </Box>
  );
};

export default MetalCollection;
